
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Move, 
  User, 
  Archive,
  Eye,
  MessageSquare,
  Tag,
  FileText
} from 'lucide-react';
import { useActivityStream, BoardActivity } from '@/hooks/useActivityStream';

interface ActivityFeedProps {
  boardId: string;
  className?: string;
}

const activityIcons: Record<string, React.ReactNode> = {
  'card_created': <Plus className="h-3 w-3" />,
  'card_updated': <Edit3 className="h-3 w-3" />,
  'card_deleted': <Trash2 className="h-3 w-3" />,
  'card_moved': <Move className="h-3 w-3" />,
  'card_archived': <Archive className="h-3 w-3" />,
  'card_assigned': <User className="h-3 w-3" />,
  'card_viewed': <Eye className="h-3 w-3" />,
  'column_created': <Plus className="h-3 w-3" />,
  'column_updated': <Edit3 className="h-3 w-3" />,
  'column_deleted': <Trash2 className="h-3 w-3" />,
  'board_updated': <Edit3 className="h-3 w-3" />,
  'comment_added': <MessageSquare className="h-3 w-3" />,
  'tag_added': <Tag className="h-3 w-3" />
};

const activityColors: Record<string, string> = {
  'card_created': 'bg-green-100 text-green-800 border-green-200',
  'card_updated': 'bg-blue-100 text-blue-800 border-blue-200',
  'card_deleted': 'bg-red-100 text-red-800 border-red-200',
  'card_moved': 'bg-purple-100 text-purple-800 border-purple-200',
  'card_archived': 'bg-gray-100 text-gray-800 border-gray-200',
  'card_assigned': 'bg-orange-100 text-orange-800 border-orange-200',
  'card_viewed': 'bg-slate-100 text-slate-800 border-slate-200',
  'column_created': 'bg-green-100 text-green-800 border-green-200',
  'column_updated': 'bg-blue-100 text-blue-800 border-blue-200',
  'column_deleted': 'bg-red-100 text-red-800 border-red-200',
  'board_updated': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'comment_added': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  'tag_added': 'bg-yellow-100 text-yellow-800 border-yellow-200'
};

function formatActivityMessage(activity: BoardActivity): string {
  const { activity_type, entity_type, details } = activity;
  
  switch (activity_type) {
    case 'card_created':
      return `created card "${details?.title || 'Untitled'}"`;
    case 'card_updated':
      return `updated card "${details?.title || 'Untitled'}"`;
    case 'card_deleted':
      return `deleted card "${details?.title || 'Untitled'}"`;
    case 'card_moved':
      return `moved card "${details?.title || 'Untitled'}" to "${details?.to_column || 'column'}"`;
    case 'card_archived':
      return `archived card "${details?.title || 'Untitled'}"`;
    case 'card_assigned':
      return `assigned card "${details?.title || 'Untitled'}" to ${details?.assigned_to || 'someone'}`;
    case 'card_viewed':
      return `viewed card "${details?.title || 'Untitled'}"`;
    case 'column_created':
      return `created column "${details?.title || 'Untitled'}"`;
    case 'column_updated':
      return `updated column "${details?.title || 'Untitled'}"`;
    case 'column_deleted':
      return `deleted column "${details?.title || 'Untitled'}"`;
    case 'board_updated':
      return `updated board settings`;
    case 'comment_added':
      return `added a comment to "${details?.title || 'card'}"`;
    case 'tag_added':
      return `added tag "${details?.tag_name || 'tag'}" to "${details?.title || 'card'}"`;
    default:
      return `performed ${activity_type.replace('_', ' ')}`;
  }
}

export function ActivityFeed({ boardId, className }: ActivityFeedProps) {
  const { activities, isLoading, isConnected } = useActivityStream({ boardId, limit: 30 });

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="animate-pulse space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-2">
              <div className="h-8 w-8 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-1">
                <div className="h-3 bg-gray-200 rounded w-3/4" />
                <div className="h-2 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Connection status */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Recent Activity
        </h3>
        <div className="flex items-center gap-1">
          <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-xs text-gray-500">
            {isConnected ? 'Live' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Activity list */}
      <ScrollArea className="h-80">
        <div className="space-y-3">
          {activities.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">No recent activity</p>
            </div>
          ) : (
            activities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3 text-sm">
                {/* User avatar */}
                <Avatar className="h-6 w-6 flex-shrink-0">
                  <AvatarFallback className="text-xs bg-gray-100 dark:bg-gray-700">
                    {(activity.user_name || 'U').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  {/* Activity message */}
                  <div className="flex items-center gap-2 mb-1">
                    <Badge 
                      variant="secondary" 
                      className={`h-5 px-1.5 text-xs ${activityColors[activity.activity_type] || 'bg-gray-100 text-gray-800'}`}
                    >
                      {activityIcons[activity.activity_type] || <FileText className="h-3 w-3" />}
                    </Badge>
                    <span className="text-gray-900 dark:text-gray-100 truncate">
                      <span className="font-medium">{activity.user_name}</span>
                      {' '}
                      {formatActivityMessage(activity)}
                    </span>
                  </div>

                  {/* Timestamp */}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
